import React, { useMemo, useState } from "react";
import {
  Card,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import SearchIcon from "@mui/icons-material/Search";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { exportMatrixToExcel } from "./cashFlowMatrixExport";
import { formatAmount } from "./cashFlowUtils";

const STICKY_COL_WIDTH = 400;

const ProveedoresTable = ({
  theme,
  mapByProveedor,
  proveedorDays,
  formatValue,
  year,
  importeVencido = [],
}) => {
  const [query, setQuery] = useState("");
  const proveedoresFiltrados = useMemo(() => {
    const q = String(query || "")
      .trim()
      .toLowerCase();
    const all = Object.keys(mapByProveedor || {});
    if (!q) return all;
    return all.filter((prov) => prov.toLowerCase().includes(q));
  }, [mapByProveedor, query]);

  const stickyBg = theme.palette.cfdia;
  const oddRowBg = theme.palette.mode === "dark" ? "#2a2a2a" : "#f4f6f8";

  const stickyHeaderSx = {
    fontWeight: "bold",
    position: "sticky",
    left: 0,
    top: 0,
    zIndex: 4,
    minWidth: STICKY_COL_WIDTH,
    maxWidth: STICKY_COL_WIDTH,
    width: STICKY_COL_WIDTH,
    bgcolor: stickyBg,
    borderBottom: "1px solid",
    borderRight: "1px solid",
    borderColor: "divider",
    boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
  };

  const stickyBodySx = {
    fontWeight: "bold",
    position: "sticky",
    left: 0,
    zIndex: 2,
    minWidth: STICKY_COL_WIDTH,
    maxWidth: STICKY_COL_WIDTH,
    width: STICKY_COL_WIDTH,
    bgcolor: stickyBg,
    borderBottom: "1px solid",
    borderRight: "1px solid",
    borderColor: "divider",
    boxShadow: "2px 0 6px rgba(15,23,42,0.08)",
  };

  const dateHeaderSx = {
    position: "sticky",
    top: 0,
    zIndex: 3,
    bgcolor: theme.palette.background.paper,
    fontSize: "0.75rem",
    textAlign: "right",
    whiteSpace: "nowrap",
    borderBottom: "1px solid",
    borderColor: "divider",
  };

  const handleExport = () => {
    exportMatrixToExcel({
      entities: proveedoresFiltrados,
      days: proveedorDays,
      map: mapByProveedor,
      entityLabel: "Proveedor",
      fileName: `facturas_proveedores_${year}.xlsx`,
    });
  };

  return (
    <Card
      sx={{
        mb: 2,
        p: 2,
        width: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 8px 20px rgba(15,23,42,0.08)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1.25,
          gap: 1,
          flexWrap: "wrap",
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Facturas por Proveedor
          </Typography>
          <Chip
            size="small"
            color="secondary"
            variant="outlined"
            label={`${proveedoresFiltrados.length} / ${Object.keys(mapByProveedor).length}`}
            sx={{ fontWeight: 700 }}
          />
          {importeVencido.map(({ year: vYear, amount }) => (
            <Chip
              key={vYear}
              size="small"
              variant="outlined"
              label={`Vencido ${vYear}: ${formatAmount(amount)}`}
              sx={{
                fontWeight: 700,
                color: amount > 0 ? "error.dark" : "text.secondary",
                borderColor: amount > 0 ? "error.main" : "divider",
              }}
            />
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Buscar proveedor..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            sx={{ height: "40px" }}
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={!proveedoresFiltrados.length}
          >
            Excel
          </Button>
        </Box>
      </Box>

      <TableContainer
        sx={{
          overflow: "auto",
          maxHeight: "70vh",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: STICKY_COL_WIDTH + proveedorDays.length * 90 }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={stickyHeaderSx}>Proveedor</TableCell>
              {proveedorDays.map((d) => (
                <TableCell key={d} sx={dateHeaderSx}>
                  {dayjs(d).format("DD/MM")}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {proveedoresFiltrados.map((prov) => (
              <TableRow
                key={prov}
                sx={{
                  "&:nth-of-type(odd)": { backgroundColor: oddRowBg },
                  "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
                }}
              >
                <TableCell className="sticky-col" sx={stickyBodySx}>
                  {prov}
                </TableCell>
                {proveedorDays.map((d) => (
                  <TableCell
                    key={d}
                    sx={{
                      fontSize: "0.8rem",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      color:
                        mapByProveedor[prov]?.[d] < 0
                          ? theme.palette.error.main
                          : undefined,
                    }}
                  >
                    {mapByProveedor[prov]?.[d]
                      ? formatValue(mapByProveedor[prov][d])
                      : ""}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default ProveedoresTable;
